<template>
  <div class="progress" :style="progressStyles">
    <div class="progress-bar">
      <div v-if="alwaysShowStripes || percentage !== 1" class="stripes"/>
    </div>
  </div>
</template>

<script setup>
import {computed} from "vue";

const props = defineProps({
  percentage: {
    type: Number,
    required: true,
    validator: (value) => value >= 0 && value <= 1,
  },
  progressBarHeight: {
    type: String,
    default: "0.5rem",
  },
  customBarColor: String,
  customTrackColor: String,
  customBorderRadiusPx: Number,
  customStripeColor: String,
  customPadding: String,
  alwaysShowStripes: Boolean,
  dark: Boolean,
});

const barColor = computed(() => {
  if (props.customBarColor) return props.customBarColor;
  if (props.percentage > .66) return "linear-gradient(135deg, #f39927 0%, #ce3154 100%)";
  if (props.percentage > .33) return "linear-gradient(135deg, #ce3154 0%, #9123ce 100%)";
  return "linear-gradient(135deg, #9123ce 0%, #0db4ec 100%)";
});

const progressTrackColor = computed(() => {
  if (props.customTrackColor) return props.customTrackColor;
  return props.dark ? "#343a40" : "#e2e8f0";
});

const borderRadius = computed(() => {
  if (props.customBorderRadiusPx) return props.customBorderRadiusPx;
  return 12;
});

const stripeColor = computed(() => {
  if (props.customStripeColor) return props.customStripeColor;
  return "rgba(255, 255, 255, 0.15)";
});

const progressPadding = computed(() => {
  if (props.customPadding) return props.customPadding;
  return "3px";
});

const progressStyles = computed(() => {
  return `--percentage:${props.percentage};--bar-color:${barColor.value};--progress-bar-height:${props.progressBarHeight};--progress-track-color:${progressTrackColor.value};--outer-border-radius:${Math.max(borderRadius.value, 0)}px;--inner-border-radius:${Math.max(borderRadius.value - 2, 0)}px;--stripe-color:${stripeColor.value};--progress-padding:${progressPadding.value}`;
});
</script>

<style scoped>
.progress {
  box-sizing: content-box;
  padding: var(--progress-padding);
  background: var(--progress-track-color);
  border-radius: var(--outer-border-radius);
  -webkit-box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.25), 0 1px rgba(255, 255, 255, 0.08);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.25), 0 1px rgba(255, 255, 255, 0.08);
  overflow: hidden;
}

.progress-bar {
  height: var(--progress-bar-height);
  border-radius: var(--inner-border-radius);
  background-image: -webkit-linear-gradient(top, rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.05));
  background-image: -moz-linear-gradient(top, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.05));
  background-image: -o-linear-gradient(top, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.05));
  background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.05));
  -webkit-transition: 0.4s linear;
  -moz-transition: 0.4s linear;
  -o-transition: 0.4s linear;
  transition: 0.4s linear;
  -webkit-transition-property: width, background-color;
  -moz-transition-property: width, background-color;
  -o-transition-property: width, background-color;
  transition-property: width, background-color;
  -webkit-box-shadow: 0 0 1px 1px rgba(0, 0, 0, 0.25), inset 0 1px rgba(255, 255, 255, 0.1);
  box-shadow: 0 0 1px 1px rgba(0, 0, 0, 0.25), inset 0 1px rgba(255, 255, 255, 0.1);
}

.progress > .progress-bar {
  max-width: 100%;
  width: calc(var(--percentage) * 100%);
  background: var(--bar-color);
  overflow: hidden;
}

.stripes {
  width: 100%;
  height: 100%;
  background-image: repeating-linear-gradient(-45deg, transparent 0 0.3125rem, var(--stripe-color) 0.3125rem 0.645rem);
  animation: progressSlide 1.5s linear infinite;
}

@keyframes progressSlide {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 15px 0;
  }
}
</style>
